import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface ITowOnTypeEntityAttributes extends IModelAttributes {
	label: string;
	disabled: boolean;
	isRestricted: boolean;

	minimumLengthId?: string;
	minimumLength?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	minimumWeightId?: string;
	minimumWeight?: Models.MeasurementEntity | Models.IMeasurementEntityAttributes;
	minimumWidthId?: string;
	minimumWidth?: Models.WidthEntity | Models.IWidthEntityAttributes;
	exactWeight?: number | null;
	exactWeightUnit?: string | null;
	towOns: Array<Models.TowOnEntity | Models.ITowOnEntityAttributes>;
	cargoCharacteristics?: Array<Models.TowOnTypeCargoCharacteristic | Models.ITowOnTypeCargoCharacteristicAttributes>;
}
@entity('TowOnTypeEntity', 'Tow On Type')
export default class TowOnTypeEntity extends Model implements ITowOnTypeEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The label that will display around the application for this tow on type.
	 */
	@observable
	@attribute<TowOnTypeEntity, string>()
	public label: string;

	/**
	 * Whether or not this tow on option is disabled from the frontend of the application.
	 */
	@observable
	@attribute<TowOnTypeEntity, boolean>()
	public disabled: boolean;

	/**
	 * Whether or not this tow on option is for only staff to book.
	 */
	@observable
	@attribute<TowOnTypeEntity, boolean>()
	public isRestricted: boolean;

	@observable
	@attribute()
	public minimumLengthId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumLength: Models.MeasurementEntity;

	@observable
	@attribute()
	public minimumWeightId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumWeight: Models.MeasurementEntity;

	@observable
	@attribute()
	public minimumWidthId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public minimumWidth: Models.WidthEntity;

	@observable
	@attribute<TowOnTypeEntity, number | null>()
	public exactWeight: number | null;

	@observable
	@attribute<TowOnTypeEntity, string | null>()
	public exactWeightUnit: string | null;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public towOns: Models.TowOnEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public cargoCharacteristics: Models.TowOnTypeCargoCharacteristic[] = [];

	constructor(attributes?: Partial<ITowOnTypeEntityAttributes>) {
		super(attributes);

		this.towOns = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<ITowOnTypeEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.label !== undefined) {
				this.label = attributes.label;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.isRestricted !== undefined) {
				this.isRestricted = attributes.isRestricted;
			}
			if (attributes.minimumLengthId !== undefined) {
				this.minimumLengthId = attributes.minimumLengthId;
			}
			if (attributes.minimumLength !== undefined) {
				if (attributes.minimumLength === null) {
					this.minimumLength = attributes.minimumLength;
				} else if (attributes.minimumLength instanceof Models.MeasurementEntity) {
					this.minimumLength = attributes.minimumLength;
					this.minimumLengthId = attributes.minimumLength.id;
				} else {
					this.minimumLength = new Models.MeasurementEntity(attributes.minimumLength);
					this.minimumLengthId = this.minimumLength.id;
				}
			}
			if (attributes.minimumWeightId !== undefined) {
				this.minimumWeightId = attributes.minimumWeightId;
			}
			if (attributes.minimumWeight !== undefined) {
				if (attributes.minimumWeight === null) {
					this.minimumWeight = attributes.minimumWeight;
				} else if (attributes.minimumWeight instanceof Models.MeasurementEntity) {
					this.minimumWeight = attributes.minimumWeight;
					this.minimumWeightId = attributes.minimumWeight.id;
				} else {
					this.minimumWeight = new Models.MeasurementEntity(attributes.minimumWeight);
					this.minimumWeightId = this.minimumWeight.id;
				}
			}
			if (attributes.minimumWidthId !== undefined) {
				this.minimumWidthId = attributes.minimumWidthId;
			}
			if (attributes.minimumWidth !== undefined) {
				if (attributes.minimumWidth === null) {
					this.minimumWidth = attributes.minimumWidth;
				} else if (attributes.minimumWidth instanceof Models.WidthEntity) {
					this.minimumWidth = attributes.minimumWidth;
					this.minimumWidthId = attributes.minimumWidth.id;
				} else {
					this.minimumWidth = new Models.WidthEntity(attributes.minimumWidth);
					this.minimumWidthId = this.minimumWidth.id;
				}
			}
			if (attributes.exactWeight !== undefined) {
				this.exactWeight = attributes.exactWeight;
			}
			if (attributes.exactWeightUnit !== undefined) {
				this.exactWeightUnit = attributes.exactWeightUnit;
			}
			if (attributes.towOns !== undefined && Array.isArray(attributes.towOns)) {
				if (!this.towOns) {
					this.towOns = [];
				}
				for (const model of attributes.towOns) {
					if (model instanceof Models.TowOnEntity) {
						this.towOns.push(model);
					} else {
						this.towOns.push(new Models.TowOnEntity(model));
					}
				}
			}
			if (attributes.cargoCharacteristics !== undefined && Array.isArray(attributes.cargoCharacteristics)) {
				if (!this.cargoCharacteristics) {
					this.cargoCharacteristics = [];
				}
				for (const model of attributes.cargoCharacteristics) {
					if (model instanceof Models.TowOnTypeCargoCharacteristic) {
						this.cargoCharacteristics.push(model);
					} else {
						this.cargoCharacteristics.push(new Models.TowOnTypeCargoCharacteristic(model));
					}
				}
			}
		}
	}
}
